import { HeroImage } from '@/components/display/hero-image';
import { ImageViewer } from '@/components/display/image-viewer';
import { QueryState } from '@/components/layout/query-state';
import { PageTitle } from '@/components/seo/page-title';
import { Button } from '@/components/ui/button';
import { Rating } from '@/components/ui/rating-distribution';
import { useBusinessDetail, useFailedImageIds } from '@/features/business';
import { SaveToListSheet } from '@/features/lists/components/save-to-list-sheet';
import { useUnifiedColorScheme } from '@/hooks/use-unified-color-scheme';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { BusinessContactSection } from './business-contact-section';
import { BusinessLocationSection } from './business-location-section';

export function BusinessDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  useUnifiedColorScheme();

  const {
    business, isLoading, error, refetch,
    displayPhotos, totalPhotoCount,
    menuItems, menuItemsTotal,
    handleWebsitePress, handlePhonePress, handleEmailPress, handleMapsPress,
  } = useBusinessDetail(id || '');

  const photoErrors = useFailedImageIds();
  const menuItemErrors = useFailedImageIds();

  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [saveSheetVisible, setSaveSheetVisible] = useState(false);

  return (
    <QueryState
      isLoading={isLoading}
      error={error}
      onRetry={() => refetch()}
      loadingMessage="Loading business details..."
      fullScreen={false}
    >
      {business && (
        <View className="flex-1">
          <PageTitle title={business.name || "Restaurant"} />

          <ScrollView showsVerticalScrollIndicator={false}>
            <HeroImage
              imageUrl={business.coverPhotoUrl ? imageUrl(business.coverPhotoUrl, 'full') : null}
              title={business.name}
              subtitle={business.city?.toUpperCase()}
              tags={business.cuisineType ? [business.cuisineType] : business.tags}
              placeholderName={business.name}
            />

            <View className="px-4 pt-2">
              <View className="flex flex-row justify-between my-3">
                <Pressable
                  className="p-2"
                  onPress={() => router.push({
                    pathname: '/(root)/business/reviews/[id]',
                    params: { id: id! },
                  })}
                >
                  <Rating distribution={business.ratingDistribution ?? null} />
                </Pressable>

                <View className="grow" />

                <View className="flex-row items-end gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onPress={() => router.push({
                      pathname: '/(root)/check-in',
                      params: { businessId: business.id }
                    })}
                  >
                    <Ionicons name="restaurant" size={16} className="text-primary-foreground" />
                    <Text className="text-sm font-semibold text-primary-foreground">Check-in</Text>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => setSaveSheetVisible(true)}
                  >
                    <Ionicons name="bookmark-outline" size={16} className="text-foreground" />
                  </Button>
                </View>
              </View>

              {/* Photo Strip */}
              {displayPhotos.length > 0 && (
                <View className="mb-4">
                  <View className="flex flex-row items-end mb-3">
                    <Text className="text-2xl font-semibold text-foreground grow" accessibilityRole="header">
                      Photos
                    </Text>
                    <Text className="text-base text-muted-foreground/50">
                      {totalPhotoCount} photos
                    </Text>
                  </View>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{ gap: 8 }}
                  >
                    {displayPhotos.map((photo, index) => (
                      <Pressable
                        key={photo.id}
                        onPress={() => {
                          setViewerIndex(index);
                          setViewerVisible(true);
                        }}
                      >
                        {photoErrors.hasFailed(photo.id) ? (
                          <View className="rounded-lg bg-surface-secondary items-center justify-center size-[120px]">
                            <Ionicons name="image-outline" size={24} className="text-muted-foreground" />
                          </View>
                        ) : (
                          <Image
                            source={{ uri: photo.imageUrl }}
                            className="rounded-lg bg-surface size-[120px]"
                            resizeMode="cover"
                            accessibilityLabel={`${business.name} photo ${index + 1}`}
                            onError={() => photoErrors.markFailed(photo.id)}
                          />
                        )}
                      </Pressable>
                    ))}
                    {totalPhotoCount > displayPhotos.length && (
                      <Pressable
                        onPress={() => router.push({
                          pathname: '/(root)/business/photos/[id]',
                          params: { id: id! },
                        })}
                        className="rounded-lg bg-surface items-center justify-center size-[120px]"
                      >
                        <Ionicons name="grid-outline" size={24} className="text-muted-foreground" />
                        <Text className="text-xs text-muted-foreground mt-1">
                          See all
                        </Text>
                      </Pressable>
                    )}
                  </ScrollView>
                </View>
              )}

              {/* Description */}
              {business.description && (
                <Text className="text-base mb-4 leading-6 text-foreground">
                  {business.description}
                </Text>
              )}

              {/* Menu Items */}
              {menuItems.length > 0 && (
                <View className="mb-4">
                  <Text className="text-2xl font-semibold mb-3 text-foreground" accessibilityRole="header">
                    Menu
                  </Text>
                  {menuItems.map((item) => (
                    <Pressable
                      key={item.id}
                      onPress={() => router.push({
                        pathname: '/(root)/menu-item/[id]',
                        params: { id: item.id }
                      })}
                      className="flex flex-row p-3 rounded-xl mb-2 bg-card border border-border"
                    >
                      <View>
                        {item.coverPhotoUrl && !menuItemErrors.hasFailed(item.id) ? (
                          <Image
                            source={{ uri: imageUrl(item.coverPhotoUrl, 'thumbnail') }}
                            className="rounded-lg bg-surface"
                            style={{ width: 64, height: 64 }}
                            resizeMode="cover"
                            accessibilityLabel={`Photo of ${item.name}`}
                            onError={() => menuItemErrors.markFailed(item.id)}
                          />
                        ) : (
                          <View
                            className="rounded-lg bg-surface-secondary items-center justify-center"
                            style={{ width: 64, height: 64 }}
                          >
                            <Ionicons name="image-outline" size={24} className="text-muted-foreground" />
                          </View>
                        )}
                      </View>

                      <View className="ml-3 flex flex-col flex-1">
                        <View className="flex flex-row">
                          <Text className="font-semibold text-lg text-foreground" numberOfLines={1}>
                            {item.name}
                          </Text>
                        </View>
                        <View className="flex-row">
                          {item.description && (
                            <Text className="text-sm mt-0.5 text-muted-foreground/50" numberOfLines={2}>
                              {item.description}
                            </Text>
                          )}
                        </View>
                      </View>

                      <View className="self-center">
                        <Rating distribution={item.ratingDistribution ?? null} size="sm" />
                      </View>
                    </Pressable>
                  ))}

                  {menuItemsTotal > 5 && (
                    <Pressable
                      onPress={() => router.push({
                        pathname: '/(root)/business/menu-items/[id]',
                        params: { id: id! },
                      })}
                      className="flex-row items-center justify-center p-3 rounded-xl bg-surface border border-border"
                    >
                      <Text className="text-sm font-medium text-primary">
                        View all {menuItemsTotal} items
                      </Text>
                    </Pressable>
                  )}
                </View>
              )}

              <BusinessLocationSection business={business} onMapsPress={handleMapsPress} />
              <BusinessContactSection
                business={business}
                onWebsitePress={handleWebsitePress}
                onPhonePress={handlePhonePress}
                onEmailPress={handleEmailPress}
              />
            </View>
          </ScrollView>

          <ImageViewer
            photos={displayPhotos}
            initialIndex={viewerIndex}
            visible={viewerVisible}
            onClose={() => setViewerVisible(false)}
          />

          <SaveToListSheet
            itemType="business"
            itemId={id || ''}
            visible={saveSheetVisible}
            onClose={() => setSaveSheetVisible(false)}
          />
        </View>
      )}
    </QueryState>
  );
}
